import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePortfolio } from "@/context/PortfolioContext";
import PortfolioSwitcher from "@/components/PortfolioSwitcher";
import { LayoutDashboard, ListChecks, IndianRupee, LogOut, Sparkles } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/transactions", label: "Transactions", icon: ListChecks, testid: "nav-transactions" },
  { to: "/prices", label: "Prices", icon: IndianRupee, testid: "nav-prices" },
];

export default function Layout({ children, title, subtitle, actions }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { corpActions } = usePortfolio();
  const navigate = useNavigate();
  const pendingCorp = corpActions?.counts?.pending || 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-40"
        style={{ background: "var(--bg-base)", borderBottom: "1px solid var(--border-soft)" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4 md:gap-8 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center shrink-0"
              data-testid="brand-home"
            >
              <img
                src="/brand/logo-dark.png"
                alt="EquityBox — Vedansh Wealth LLP"
                className="h-10 w-auto"
                style={{ maxHeight: 40 }}
                data-testid="brand-logo"
              />
            </button>
            {/* Desktop nav (pills) */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map(({ to, label, icon: Icon, testid }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={testid}
                    className="px-3.5 py-1.5 rounded text-[13px] font-medium transition-colors duration-200 inline-flex items-center gap-2"
                    style={{
                      color: active ? "var(--accent-primary)" : "var(--text-secondary)",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {pendingCorp > 0 && (
              <button
                data-testid="corp-actions-badge"
                onClick={() => navigate("/dashboard")}
                title={`${pendingCorp} corporate action${pendingCorp > 1 ? "s" : ""} to review`}
                className="relative inline-flex items-center gap-1.5 text-[13px] px-2.5 md:px-3 py-1.5 rounded border transition-colors hover:bg-white/5"
                style={{ borderColor: "var(--accent-primary)", color: "var(--accent-primary)" }}
              >
                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span className="hidden sm:inline">Actions</span>
                <span
                  data-testid="corp-actions-badge-count"
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-mono font-medium"
                  style={{ background: "var(--accent-primary)", color: "var(--btn-primary-fg)" }}
                >
                  {pendingCorp}
                </span>
              </button>
            )}
            <PortfolioSwitcher />
            {user && (
              <div className="hidden lg:flex items-center gap-2.5 pr-3" data-testid="user-pill">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs"
                    style={{ background: "var(--accent-primary)", color: "var(--btn-primary-fg)" }}
                  >
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="leading-tight">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {user.email}
                  </div>
                </div>
              </div>
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              aria-label="Logout"
              className="inline-flex items-center gap-2 text-[13px] px-2.5 md:px-3 py-1.5 rounded border transition-colors hover:bg-white/5"
              style={{ borderColor: "var(--btn-secondary-border)", color: "var(--text-secondary)" }}
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Page header */}
      {(title || actions) && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 md:pt-10 pb-4 md:pb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            {subtitle && <p className="label-overline mb-2">{subtitle}</p>}
            {title && (
              <h1 className="font-display text-[26px] sm:text-[30px] font-bold tracking-[-0.025em] leading-[1.15]">
                {title}
              </h1>
            )}
          </div>
          {actions && (
            <div className="flex items-center flex-wrap gap-2 md:gap-3" data-testid="page-actions">
              {actions}
            </div>
          )}
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-8 pb-24 md:pb-16">{children}</main>

      {/* Mobile bottom tab bar (thumb-friendly) */}
      <nav
        data-testid="mobile-tabbar"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch"
        style={{
          background: "var(--bg-base)",
          borderTop: "1px solid var(--border-soft)",
          paddingBottom: "env(safe-area-inset-bottom, 0)",
        }}
      >
        {NAV.map(({ to, label, icon: Icon, testid }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] font-mono tracking-[0.06em] uppercase transition-colors"
              style={{
                color: active ? "var(--accent-primary)" : "var(--text-muted)",
              }}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              {label}
              <span
                className="block w-1.5 h-1.5 rounded-sm mt-0.5"
                style={{ background: active ? "var(--accent-primary)" : "transparent" }}
              />
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
